import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Image } from "@/components/common/image";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { DownloadIcon, MoreIcon, TrashIcon } from "@/components/ui/icon";
import { ModalCloseButton } from "@/components/common/modal-close-button";
import { useDownload } from "@/hooks/use-download";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { PublishIcon } from "@/components/agent-share-modal/assets";
import { TextButton } from "@/components/common/text-button";
import { IconButton } from "@/components/common/icon-button";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { Edit3, RefreshCcw } from "lucide-react";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { AccountCard } from "../common/account-card";
import { CopyClipboard } from "../common/copy-clipboard";

export interface AgentMomentModalProps {
  open: boolean;
  // 右侧信息
  avatarUrl: string;
  nickname: string;
  text: string;
  images: string[];
  deleteLoading?: boolean;
  isAutoPush?: boolean;
  onOpenChange: (open: boolean) => void;
  onCopyText?: (text: string) => void;
  onDownloadAll?: (images: string[]) => void;
  onReedit?: () => void;
  onDelete?: () => Promise<void>;
  onRegenerate?: () => void;
}

const CopySvg = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
);

export const AgentMomentModal: React.FC<AgentMomentModalProps> = ({
  open,
  avatarUrl,
  nickname,
  text,
  images,
  deleteLoading,
  isAutoPush,
  onOpenChange,
  onCopyText,
  onDownloadAll,
  onReedit,
  onDelete,
  onRegenerate,
}) => {
  const { t } = useTranslation();
  const imageRef = useRef<HTMLImageElement>(null);
  const { download, downloadZip } = useDownload();
  const { toast } = useToast();

  const handleCopy = async () => {
    try {
      if (onCopyText) {
        await Promise.resolve(onCopyText(text));
        toast.default({ description: t('agent_creation.agent_modal.copied') });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast.default({ description: t('agent_creation.agent_modal.copied') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.copy_fail') });
    }
  };

  const handleDownloadAll = async () => {
    if (!isImages) return;
    try {
      const list = images.slice(0, 9).map((url, i) => ({ url, filename: `image_${i + 1}.jpg` }));
      if (onDownloadAll) {
        await Promise.resolve(onDownloadAll(images.slice(0, 9)));
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
        return;
      }
      await downloadZip(list, "images.zip");
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') setSelectedIndex((i) => (i - 1 + images.length) % images.length);
      if (e.key === 'ArrowRight') setSelectedIndex((i) => (i + 1) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, images.length]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [open]);

  // 新增：下载当前单张图片
  const handleDownloadCurrent = async () => {
    const url = images[selectedIndex];
    if (!url) return;
    try {
      await download(url, `image_${selectedIndex + 1}.jpg`);
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.error({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };

  // 判断是否有图片
  const isImages = useMemo(() => {
    return !(images?.length === 1 && !images?.[0]) && images.length > 0;
  }, [images]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-2xl shadow-lg z-50 flex h-[90vh]"
        >
          {/* 左侧：单张图展示，黑底 */}
          {isImages && (
            <div
              className="group relative h-full aspect-[3/4] bg-black flex items-center justify-center shrink-0 grow-0"
            >
              {/* 叠加：当前/总数 */}
              <div className="absolute top-3 right-3 px-2 py-1 rounded bg-black/60 text-white text-sm z-10">
                {selectedIndex + 1}/{images.length}
              </div>

              {/* 叠加：左右轮播按钮（透明黑底白色图标） */}
              <button
                onClick={() => setSelectedIndex((i) => (i - 1 + images.length) % images.length)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded bg-black/40 hover:bg-black/60 text-white flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Previous"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
              <button
                onClick={() => setSelectedIndex((i) => (i + 1) % images.length)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded bg-black/40 hover:bg-black/60 text-white flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Next"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>

              {/* 叠加：右下角下载单张图片 */}
              <button
                onClick={handleDownloadCurrent}
                className="absolute bottom-4 right-4 px-3 py-2 rounded-md bg-black/60 hover:bg-black/70 text-white flex items-center gap-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <DownloadIcon className="w-5 h-5" />
                <span className="text-sm">{t('agent_creation.agent_modal.download_single_image')}</span>
              </button>

              <div className="relative w-full h-full flex items-center justify-center">
                <Image
                  ref={imageRef}
                  src={images[selectedIndex]}
                  className="h-full w-auto object-contain"
                  alt="moment-cover"
                  crossOrigin="anonymous"
                />
              </div>
            </div>
          )}

          <div className={cn(
            "w-[430px] h-full bg-white text-[#111827] flex flex-col shrink-0 grow-0",
            !isImages && "w-[450px]"
          )}>
            <div className="flex justify-between items-center p-4">
              <div className="flex items-center gap-3">
                <AccountCard
                  compact
                  className="size-10"
                  name={nickname}
                  avatar={avatarUrl}
                />
                <div className="text-lg font-semibold">{nickname}</div>
              </div>
              <ModalCloseButton onClick={() => onOpenChange(false)} />
            </div>

            {/* 文案 */}
            <div className="p-4 pt-2 text-[15px] leading-7">{text}</div>

            {/* 九宫格 */}
            {isImages && <div
              className="px-4 pb-2"
            >
              <div
                className="grid gap-1 w-[220px]"
                style={{ gridTemplateColumns: `repeat(3, minmax(0, 1fr))` }}
              >
                {images.slice(0, 9).map((url, i) => {
                  const isSelected = i === selectedIndex;
                  return (
                    <div
                      key={i}
                      role="button"
                      tabIndex={0}
                      onClick={() => { setSelectedIndex(i); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setSelectedIndex(i); } }}
                      aria-selected={isSelected}
                      className={`w-full rounded-md cursor-pointer ${isSelected ? "ring-2 ring-[#6366F1]" : ""}`}
                    >
                      <Image
                        src={url}
                        alt={`img-${i}`}
                        className="w-full aspect-square object-cover rounded-md"
                      />
                    </div>
                  );
                })}
              </div>
            </div>}

            <div className="mt-auto flex justify-end items-center gap-3 px-6 pb-5 pt-4">
              <MoreActionPopover
                className="w-40"
                side="top"
                align="center"
                tiggerClassName="size-9 rounded-[8px]"
                tiggerIcon={
                  <IconButton
                    icon={<MoreIcon className="size-5 !text-[rgba(51,51,51,1)]" />}
                  />
                }
              >
                {!isAutoPush && (
                  <>
                    <MoreActionItem
                      icon={<Edit3 className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("agent_creation.content.reedit")}
                      onClick={() => {
                        onReedit?.();
                        onOpenChange(false);
                      }}
                    />
                    {/* <MoreActionItem
                      icon={<RefreshCcw className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("agent_creation.content.regenerate_again")}
                      onClick={() => {
                        onRegenerate?.();
                        onOpenChange(false);
                      }}
                    /> */}
                  </>
                )}
                <ConfirmModal
                  trigger={
                    <MoreActionItem
                      icon={<TrashIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("common.delete")}
                    />
                  }
                  title={t('agent_creation.content.delete_title')}
                  description={t('agent_creation.content.delete_desc')}
                  cancel={t('common.cancel')}
                  confirm={t('common.delete')}
                  onConfirm={async () => {
                    await onDelete?.();
                    onOpenChange(false);
                  }}
                  loading={deleteLoading}
                />
              </MoreActionPopover>
              <CopyClipboard
                className="size-9 rounded-[8px]"
                iconClassName="w-5 h-5 text-[rgba(51,51,51,1)]"
                text={text}
                icon={<CopySvg className="w-5 h-5 text-[rgba(51,51,51,1)]" />}
              />
              <TextButton
                className={cn(
                  "px-[22px] py-[10px] rounded-[8px] max-w-max text-sm",
                  !isImages && "cursor-not-allowed opacity-50"
                )}
                icon={<DownloadIcon className="w-5 h-5" />}
                label={t('common.download')}
                onClick={handleDownloadAll}
                disabled={!isImages}
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AgentMomentModal;